import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { BuilderWizard, type WizardResult } from '@/components/builder/BuilderWizard';
import { GlobeIcon } from '@/components/icons/MenuIcons';
import { useAuth } from '@/utils/AuthContext';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { builderApi, projectsApi, type Build } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

/** Generation takes ~30s; the bar is tuned a little longer so it never stalls at 95%. */
const GENERATION_ESTIMATE_SECONDS = 45;

export default function BuilderListPage() {
  const { t, i18n } = useTranslation('builder');
  const { subscriptionTier } = useAuth();
  const navigate = useLocalizedNavigate();

  const [builds, setBuilds] = useState<Build[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Build | null>(null);

  const isFree = subscriptionTier === 'free';

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setIsLoading(true);
    const [buildsResult, projectsResult] = await Promise.allSettled([
      builderApi.list(),
      projectsApi.list(),
    ]);

    if (buildsResult.status === 'fulfilled') setBuilds(buildsResult.value.builds);
    if (projectsResult.status === 'fulfilled') setProjects(projectsResult.value);
    setIsLoading(false);
  }

  async function handleCreate(result: WizardResult) {
    setIsCreating(true);
    try {
      const { build } = await builderApi.create({
        name: result.name,
        projectId: result.projectId,
        brief: result.brief,
      });

      // Generation runs straight after creation: an empty draft is not a useful
      // place to leave someone who just filled in a brief.
      const generated = await builderApi.generate(build.id, result.brief, i18n.language as 'ru' | 'en');

      toast[generated.degraded ? 'warning' : 'success'](
        generated.degraded ? t('toast.generatedDegraded') : t('toast.generated'),
      );
      navigate(`/builder/${build.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
      setIsCreating(false);
      void load();
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);

    try {
      await builderApi.delete(target.id);
      setBuilds((current) => current.filter((build) => build.id !== target.id));
      toast.success(t('toast.deleted'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('toast.error'));
    }
  }

  if (isCreating) {
    return (
      <div className="flex flex-col gap-[24px]">
        <PageHeader title={t('page.title')} />
        <GenerationProgress
          title={t('generating.title')}
          message={t('generating.message')}
          estimatedDuration={GENERATION_ESTIMATE_SECONDS}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-wrap items-start justify-between gap-[16px]">
        <PageHeader title={t('page.title')} description={t('page.description')} />
        {!isFree && builds.length > 0 && (
          <Button onClick={() => setShowWizard(true)}>{t('list.create')}</Button>
        )}
      </div>

      {isFree && <SubscriptionLimitBanner description={t('limit.free')} />}

      {isLoading ? (
        <p className="font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">…</p>
      ) : builds.length === 0 ? (
        <div className="flex flex-col items-center gap-[12px] rounded-[12px] border border-dashed border-[var(--grey-accent,#eeeeee)] px-[24px] py-[48px] text-center">
          <GlobeIcon size={32} className="text-[color:var(--grey-text,#969696)]" />
          <h2 className="font-manrope text-[20px] font-bold text-[color:var(--black-text,#0d0d0d)]">
            {t('list.empty.title')}
          </h2>
          <p className="max-w-[520px] font-segoe text-[15px] text-[color:var(--grey-text,#565858)]">
            {t('list.empty.text')}
          </p>
          {!isFree && (
            <Button className="mt-[8px]" onClick={() => setShowWizard(true)}>
              {t('list.empty.action')}
            </Button>
          )}
        </div>
      ) : (
        <ul className="grid gap-[16px] sm:grid-cols-2 xl:grid-cols-3">
          {builds.map((build) => (
            <li
              key={build.id}
              className="flex flex-col gap-[12px] rounded-[12px] border border-[var(--grey-accent,#eeeeee)] p-[20px]"
            >
              <div className="flex items-start justify-between gap-[8px]">
                <h3 className="font-manrope text-[17px] font-bold text-[color:var(--black-text,#0d0d0d)]">
                  {build.name}
                </h3>
                <span className="shrink-0 rounded-[6px] bg-[var(--grey-background,#f8f8f8)] px-[8px] py-[2px] font-segoe text-[12px] text-[color:var(--grey-text,#565858)]">
                  {t(`list.status.${build.status}`)}
                </span>
              </div>

              {/* Живой сайт — сразу кликабельным адресом: это то, ради чего всё
                  и делалось, и его чаще всего нужно кому-то отправить. */}
              {build.url ? (
                <a
                  href={build.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate font-segoe text-[13px] text-[color:var(--main-brand,#2197f1)] hover:underline"
                >
                  {build.url.replace(/^https:\/\//, '')}
                </a>
              ) : (
                <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
                  {build.slug || t('list.card.noContent')}
                </p>
              )}

              {build.leads > 0 && (
                <p className="font-segoe text-[13px] text-[color:var(--black-text,#0d0d0d)]">
                  {t('list.card.leads', { count: build.leads })}
                </p>
              )}

              <div className="mt-auto flex gap-[8px] pt-[4px]">
                <Button size="sm" onClick={() => navigate(`/builder/${build.id}`)}>
                  {t('list.card.open')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(build)}>
                  {t('list.card.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{t('wizard.title')}</DialogTitle>
          </DialogHeader>
          <BuilderWizard
            projects={projects}
            isSubmitting={isCreating}
            onSubmit={handleCreate}
            onCancel={() => setShowWizard(false)}
          />
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title={t('list.deleteConfirm.title')}
        description={t('list.deleteConfirm.text')}
        confirmText={t('list.deleteConfirm.confirm')}
        cancelText={t('list.deleteConfirm.cancel')}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
